import { Alert, AlertDescription } from "@/components/ui/alert"
import { Skeleton } from "@/components/ui/skeleton"
import { Heading, Text } from "@/components/ui/typography"
import { sfIcons } from '@/lib/sf-icons'
import { cn } from "@/lib/utils"
import { semanticTokens } from "@supabank/config/theme"
import { lazy, Suspense } from "react"
import { useTranslation } from "react-i18next"

// Lazy load UnifiedCalculator to reduce initial bundle
const UnifiedCalculator = lazy(() =>
  import("@/components/FeeCalculator/UnifiedCalculator").then(m => ({ default: m.UnifiedCalculator }))
)

type CalculatorSectionProps = {
  id?: string
}

export function CalculatorSection({ id }: CalculatorSectionProps) {
  const { t } = useTranslation("marketing")

  return (
    <section
      id={id}
      className={cn(
        "py-16 sm:py-24",
        semanticTokens.bg.surface
      )}
      aria-labelledby="calculator-title"
    >
      <div className="mx-auto max-w-6xl px-4 sm:px-6">
        {/* Header */}
        <div className="mb-8 sm:mb-10 md:mb-12 text-center">
          <div className={cn(
            "inline-flex items-center justify-center w-12 h-12 rounded-full mb-4",
            semanticTokens.info.bg.replace("bg-", "bg-")
          )}>
            <sfIcons.calculator className={cn("h-6 w-6", semanticTokens.info.accent)} aria-hidden="true" />
          </div>
          <Heading level={2} id="calculator-title" className="mb-4">
            {t("calculatorSection.headline")}
          </Heading>
          <Text variant="lg" color="muted" className="mx-auto max-w-2xl">
            {t("calculatorSection.description")}
          </Text>
        </div>

        {/* Calculator Component */}
        <div className="max-w-3xl mx-auto">
          <Suspense
            fallback={
              <div className="space-y-4">
                <Skeleton className="h-96 w-full rounded-lg" />
              </div>
            }
          >
            <UnifiedCalculator />
          </Suspense>

          {/* Info Alert */}
          <Alert className="mt-6">
            <sfIcons.info className="h-4 w-4" />
            <AlertDescription className={semanticTokens.typography.bodySmall}>
              <strong>Transparent pricing:</strong> All fees are calculated and displayed before you confirm.
              Exchange rates update in real-time. Minimum transaction amounts vary by asset and network.
            </AlertDescription>
          </Alert>
        </div>
      </div>
    </section>
  )
}

